import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/toast';
import { useSaveTravelFee, useVoidTravelFee } from 'hooks/useInterventionRequests';
import type { FinancialOperationView, InterventionRequest } from 'api/interventionRequestsApi';
import { saveErrorMessage } from 'utils/errorMessage';
import { activeLocale } from 'i18n';
import { centsText, parseMoneyDraft, type MoneyDraft } from 'utils/money';
import TariffField from './TariffField';
import { technicianName } from './upcomingAssignment';

interface TravelFeeEditorProps {
  request: InterventionRequest;
  /** His live fee, if any: an emptied field voids it. */
  fee: FinancialOperationView | null;
  onDone: () => void;
}

/** F19 — the inline « Frais » edit of a solicited row; mounted only while editing. */
const TravelFeeEditor = ({ request, fee, onDone }: TravelFeeEditorProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveTravelFee();
  const voidFee = useVoidTravelFee();
  const [draft, setDraft] = useState<MoneyDraft>({
    text: fee ? centsText(fee.amountCents, activeLocale()) : '',
    taxBasis: fee?.taxBasis ?? 'HT',
  });
  const amountCents = parseMoneyDraft(draft.text);
  const refused = amountCents === undefined;
  const busy = save.isPending || voidFee.isPending;
  const error = save.error ?? voidFee.error;
  const target = { issueKey: request.issueKey, serviceDeskId: request.serviceDeskId };

  const settled = ({ notified }: { notified: 0 | 1 }) => {
    toast.success(
      notified === 1 ? t('Fees saved · {{name}} notified by e-mail.', { name: technicianName(request) }) : t('Fees saved.')
    );
    onDone();
  };

  const submit = () => {
    if (busy || amountCents === undefined) return;
    if (amountCents !== null) {
      save.mutate(
        { ...target, fee: { technicianUserId: request.technician.id, amountCents, taxBasis: draft.taxBasis } },
        { onSuccess: settled }
      );
    } else if (fee) {
      voidFee.mutate({ ...target, operationId: fee.id }, { onSuccess: settled });
    } else {
      onDone();
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-md border border-zinc-200 p-2.5 dark:border-zinc-800">
      <TariffField
        id={`solicited-fee-${request.id}`}
        label={t('Travel fee · optional')}
        hint={null}
        error={t('Enter an amount, e.g. 40 or 40.50.')}
        text={draft.text}
        taxBasis={draft.taxBasis}
        refused={refused}
        onTextChange={(text) => setDraft((current) => ({ ...current, text }))}
        onTaxBasisChange={(taxBasis) => setDraft((current) => ({ ...current, taxBasis }))}
      />
      {error ? (
        <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
          {saveErrorMessage(t, error)}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        <Button type="button" size="sm" className="h-8" disabled={busy || refused} onClick={submit}>
          {busy ? t('Saving changes…') : t('Save changes')}
        </Button>
        <Button type="button" variant="outline" size="sm" className="h-8" disabled={busy} onClick={onDone}>
          {t('Cancel')}
        </Button>
      </div>
    </div>
  );
};

export default TravelFeeEditor;
